import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/auth.api";
import wordmark from "../assets/images/woxa-cafe-wordmark.png";



const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [peek, setPeek] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (password !== confirm) {
      setErrorMsg("Passwords don't match.");
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword({ token, password });
      setDone(true);
    } catch (err: any) {
      const status = err?.response?.status as number | undefined;
      const code = err?.response?.data?.code as string | undefined;
      if (status === 400 && code === "INVALID_TOKEN") {
        setErrorMsg("This reset link is invalid or has expired. Please request a new one.");
      } else if (status === 400) {
        setErrorMsg(err?.response?.data?.message ?? "Invalid request. Please try again.");
      } else if (status === 429) {
        setErrorMsg("Too many attempts. Please wait a minute and try again.");
      } else if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        setErrorMsg("Can't reach the server. Check your internet connection.");
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans relative overflow-hidden">
      {/* === HERO PANEL === */}
      <div className="relative bg-hero text-hero-on px-5 pt-[44px] pb-[80px] overflow-hidden rounded-b-[40px]">
        <svg aria-hidden className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dotgrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dotgrid)" />
        </svg>
        <div className="relative">
          <div className="mb-[64px]">
            <img src={wordmark} alt="Woxa Cafe" className="h-[44px] w-auto" style={{ filter: "invert(1)" }} />
          </div>
          <p className="eyebrow-v2 text-hero-muted mb-3">Reset password</p>
          <h1 className="font-display font-black text-[40px] leading-[1.05] tracking-tightest">
            Set a new<br />password.
          </h1>
        </div>
      </div>

      {/* === FORM CARD === */}
      <div className="px-5 -mt-[48px] relative z-10">
        <div className="bg-white rounded-3xl border border-ink-100 shadow-floaty px-6 pt-[28px] pb-[24px] max-w-md mx-auto">
          {done ? (
            <div className="text-center py-2">
              <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-ink-1000" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="font-display font-black text-[22px] mb-2">Password updated</h2>
              <p className="text-sm text-ink-500 mb-6">Your password has been reset. Sign in with your new password.</p>
              <button onClick={() => navigate("/login")} className="btn-v2-primary btn-v2-lg w-full">
                Go to sign in
              </button>
            </div>
          ) : !token ? (
            <div className="text-center py-2">
              <h2 className="font-display font-black text-[22px] mb-2">Invalid link</h2>
              <p className="text-sm text-ink-500 mb-6">This reset link is missing its token. Please request a new one.</p>
              <button onClick={() => navigate("/forgot-password")} className="btn-v2-primary btn-v2-lg w-full">
                Request a new link
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-[6px]">
              <div className="field-v2 pr-[40px]">
                <input
                  id="password"
                  type={peek ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  placeholder=" "
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <label htmlFor="password">New password</label>
                <button
                  type="button"
                  onClick={() => setPeek(!peek)}
                  className="absolute right-0 bottom-1 h-9 w-9 rounded-full flex items-center justify-center text-ink-400 hover:text-ink-1000 transition"
                  aria-label={peek ? "Hide password" : "Show password"}
                >
                  {peek ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  )}
                </button>
              </div>

              <div className="field-v2">
                <input
                  id="confirm"
                  type={peek ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  placeholder=" "
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                <label htmlFor="confirm">Confirm new password</label>
              </div>



              {errorMsg && (
                <div className="pt-2 text-sm text-ink-1000 font-medium flex items-center gap-2">
                  <svg className="w-[16px] h-[16px] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {errorMsg}
                </div>
              )}

              <button type="submit" disabled={isLoading || !password || !confirm} className="btn-v2-primary btn-v2-lg w-full mt-[24px]">
                {isLoading ? (
                  <>
                    <svg className="w-[16px] h-[16px] animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" /><path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
                    Updating
                  </>
                ) : (
                  "Reset password"
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="px-5 pb-[40px] max-w-md mx-auto text-center pt-[24px]">
        <p className="text-[11px] text-ink-400">
          © {new Date().getFullYear()} Woxa Corp · Brewed with care
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
